import { captureException } from '@sentry/core';
import axios from 'axios';

import { getAuthToken, getUserLoginInfo } from '@/helpers/Authentication/authClient';
import { getCurrentPlatformTheme } from '@/helpers/platformDetection';

export interface UserProfile {
  isHomeowner?: boolean;
  howFavoritesCtaInteraction?: boolean;
}

const getUserAccountUrl = (userId: string): string =>
  `${import.meta.env.VUE_APP_API_BASE_URL}/accounts/users/${encodeURIComponent(userId)}?tenant=${getCurrentPlatformTheme()}`;

export async function getCurrentUserProfile(): Promise<UserProfile | null> {
  const userLoginInfo = await getUserLoginInfo();

  if (!userLoginInfo?.userId) {
    return null;
  }

  const authToken = await getAuthToken();

  if (!authToken) {
    return null;
  }

  try {
    const { data } = await axios.get<UserProfile>(getUserAccountUrl(userLoginInfo.userId), {
      headers: { Authorization: `Bearer ${authToken}` },
    });

    return data ?? null;
  } catch (error) {
    if ((error as { response?: { status?: number } }).response?.status !== 401) {
      captureException(error);
    }

    return null;
  }
}

async function patchCurrentUserProfile(body: Partial<UserProfile>): Promise<boolean> {
  const userLoginInfo = await getUserLoginInfo();

  if (!userLoginInfo?.userId) {
    return false;
  }

  const authToken = await getAuthToken();

  if (!authToken) {
    return false;
  }

  try {
    await axios.patch<UserProfile>(getUserAccountUrl(userLoginInfo.userId), body, {
      headers: { Authorization: `Bearer ${authToken}` },
    });

    return true;
  } catch (error) {
    if ((error as { response?: { status?: number } }).response?.status !== 401) {
      captureException(error);
    }

    return false;
  }
}

export async function updateHasAddedProperty(
  howFavoritesCtaInteraction: boolean,
): Promise<boolean> {
  return patchCurrentUserProfile({ howFavoritesCtaInteraction });
}

export async function updateIsHomeowner(isHomeowner: boolean): Promise<boolean> {
  return patchCurrentUserProfile({ isHomeowner });
}
